import * as React from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { cn } from '@restworld/utils-common';

import { StyledRadioHoverEl } from '../radio-group';
import { focusRing } from '../styles/focus-ring';

import type { JSX } from 'react';

export const Checkbox = React.forwardRef<HTMLInputElement, ICheckboxProps>((props, ref) => {
  const {
    title,
    isChecked,
    mode = 'clickable',
    id,
    onChange = () => null,
    onClick,
    value,
    field,
    iserror,
    background,
    disabled,
    testId,
    labelClassName,
    ...rest
  } = props;
  const checkboxEl = (
    <span className="relative grid place-items-center">
      <input
        type="checkbox"
        data-cy={testId}
        id={id}
        checked={isChecked}
        onChange={onChange}
        onClick={onClick}
        value={value as string}
        disabled={disabled}
        ref={ref}
        className={cn(
          'border border-neutral-90 bg-neutral-10 p-2 rounded h-[16px] w-[16px] group-hover:border-purple-500 relative z-10 focus:ring-0 disabled:bg-neutral-40 checked:!bg-purple-500 appearance-none cursor-pointer',
          iserror && 'border-red-500',
          disabled && 'checked:!bg-neutral-90'
        )}
        {...field}
        {...rest}
      />
      <StyledRadioHoverEl />
    </span>
  );
  return (
    <label
      htmlFor={id}
      className={cn(
        `group flex items-center gap-[10px] text-sm text-gray-600 font-normal w-fit ${focusRing}`,
        mode === 'static' ? 'pointer-events-none cursor-auto' : 'pointer-events-auto cursor-pointer',
        background === 'primary' && 'bg-primary-50 p-4 rounded-lg',
        background === 'primary' && isChecked && 'border border-primary-700 bg-primary-100',
        disabled
          ? 'pointer-events-none cursor-not-allowed saturate-25 opacity-60'
          : 'pointer-events-auto cursor-pointer',
        // last so callers can override the default w-fit (e.g. full-width options in a column group)
        labelClassName
      )}
    >
      {checkboxEl}
      {!!title &&
        (typeof title === 'string' ? (
          <span
            className={cn(
              '!text-base text-neutral-700 font-medium cursor-pointer',
              disabled && 'text-neutral-100 cursor-not-allowed',
              iserror && 'text-red-500'
            )}
          >
            {title}
          </span>
        ) : (
          title
        ))}
    </label>
  );
});
export default Checkbox;

type CheckboxMode = 'static' | 'clickable';
export interface ICheckboxProps {
  title?: string | JSX.Element;
  isChecked?: boolean;
  mode?: CheckboxMode;
  id?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  value?: string | boolean;
  field?: ControllerRenderProps;
  iserror?: boolean;
  background?: 'primary';
  disabled?: boolean;
  testId?: string;
  labelClassName?: string;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  ref?: React.Ref<HTMLInputElement>;
}
export type CheckBoxVariantType = 'circle' | 'box' | 'chip';
